/* eslint-disable react-hooks/exhaustive-deps */
import { Box, LinearProgress, Tooltip } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { Status, StatusCode } from '../../../api/enumerations';
import { getWorkOrdersInStatus } from '../../../api/workOrders';
import {
  isBankDossierApiEnabled,
  mergeBankDossiers,
} from '../../../api/workOrders/bankDossier';
import { WorkOrderData } from '../../../api/workOrders/types';
import { Constants } from '../../../constants/home';
import {
  IconKeyboardDoubleArrowLeftMS,
  IconKeyboardDoubleArrowRightMS,
  IconNorthdMS,
  IconSouthdMS,
} from '../../../constants/icons';
import { fetchBankDossierLookup } from '../../../hooks/useBankDossierLookup';
import useErrorMessage from '../../../hooks/useErrorMessage';
import useSnackbar from '../../../hooks/useSnackbar';
import { Card } from './Card';
import {
  CollapsedRail,
  ColumnCount,
  ColumnHeader,
  ColumnTitle,
  HeaderButton,
  PipelineBox,
  PipelinePaper,
  RailCount,
  RailIcon,
  RailLabel,
  SortIcons,
  StyledSkeleton,
} from './styles';

interface ColumnProps {
  columnData: {
    name: string;
    workOrders: WorkOrderData[];
    link: string;
    status: Status;
    totalRecords: number | undefined;
  };
  pipelineLoading: boolean;
  isSearching: boolean;
  collapsed: boolean;
  onToggleCollapse: () => void;
}

export function Column({
  columnData,
  pipelineLoading,
  isSearching,
  collapsed,
  onToggleCollapse,
}: ColumnProps): JSX.Element {
  const [workOrders, setWorkOrders] = useState<WorkOrderData[]>([]);
  const [page, setPage] = useState(2);
  const [lastCalledPage, setLastCalledPage] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [sortType, setSortType] = useState<'asc' | 'desc'>('desc');
  const [applySort, setApplySort] = useState(false);
  const [sortLoading, setSortLoading] = useState(false);

  const observer = useRef<IntersectionObserver | null>(null);

  const currentStatus = columnData.status;

  const { handleSnackbar } = useSnackbar();

  const { getErrorMessage } = useErrorMessage();

  // Pages loaded by the column itself: their dossiers fill in when the batch
  // arrives, without holding the cards back. A failure leaves them empty.
  const loadDossiers = (rows: WorkOrderData[]): void => {
    if (!isBankDossierApiEnabled || rows.length === 0) return;
    fetchBankDossierLookup(rows.map((row) => row.id))
      .then((lookup) =>
        setWorkOrders((current) => mergeBankDossiers(current, lookup))
      )
      .catch(() => undefined);
  };

  const getWorkOrderInStatus = async (): Promise<void> => {
    if (page === lastCalledPage) {
      setApplySort(false);
      setSortLoading(false);
      return;
    }

    setLoadingMore(true);
    try {
      const workOrdersPerPage = 10;
      const response = await getWorkOrdersInStatus(
        currentStatus,
        page,
        workOrdersPerPage,
        sortType
      );

      if (response.detail.description) {
        throw new Error(response.detail.description);
      }

      if (response.detail.status_code !== StatusCode.OK) {
        throw new Error('Algo deu errado, tente novamente.');
      }

      if (response.data) {
        const updatedData = [...workOrders, ...response.data];
        setWorkOrders(updatedData);
        loadDossiers(response.data);
        setLastCalledPage(page);

        if (response.detail.total_pages && response.detail.total_pages > page) {
          setPage(page + 1);
        }
        setApplySort(false);
        setLoadingMore(false);
        setSortLoading(false);
      }
    } catch (error) {
      handleSnackbar(getErrorMessage(error), true);
      setLoadingMore(false);
      setApplySort(false);
      setSortLoading(false);
    }
  };

  const lastElementRef = (node: HTMLAnchorElement): void => {
    if (loadingMore) return;

    if (observer.current) observer.current.disconnect();

    observer.current = new IntersectionObserver((entries) => {
      if (
        entries[0].isIntersecting &&
        columnData.workOrders?.length === 10 &&
        page > lastCalledPage
      ) {
        getWorkOrderInStatus();
      }
    });

    if (node) observer.current.observe(node);
  };

  const recordCount = ((): number => {
    const { totalRecords, workOrders: initial } = columnData;
    if (pipelineLoading || sortLoading) return 0;
    return totalRecords !== undefined ? totalRecords : initial?.length || 0;
  })();

  const handleSort = (): void => {
    setSortType((prevType) => (prevType === 'desc' ? 'asc' : 'desc'));
    setPage(1);
    setLastCalledPage(0);
    setWorkOrders([]);
    setApplySort(true);
  };

  // Reset only when the column's list itself changes (new search, filters or
  // a refetch), not on every parent render; an empty list must clear it too.
  useEffect(() => {
    setWorkOrders(columnData.workOrders);
    setPage(2);
    setLastCalledPage(0);
    setSortType('desc');
  }, [columnData.workOrders]);

  useEffect(() => {
    if (applySort) {
      getWorkOrderInStatus();
      setSortLoading(true);
    }
  }, [applySort]);

  const sortLabel =
    sortType === 'desc' ? Constants.sortNewest : Constants.sortOldest;

  if (collapsed) {
    return (
      <PipelineBox collapsed>
        <CollapsedRail
          onClick={onToggleCollapse}
          aria-expanded={false}
          aria-label={`${Constants.expandColumn} ${columnData.name}`}
          title={`${Constants.expandColumn} ${columnData.name}`}
        >
          <RailCount>{recordCount}</RailCount>
          <RailLabel>{columnData.name}</RailLabel>
          <RailIcon aria-hidden="true">
            {IconKeyboardDoubleArrowRightMS}
          </RailIcon>
        </CollapsedRail>
      </PipelineBox>
    );
  }

  return (
    <PipelineBox collapsed={false}>
      <ColumnHeader>
        <ColumnTitle title={columnData.name}>
          {columnData.name}
          <ColumnCount>({recordCount})</ColumnCount>
        </ColumnTitle>
        {!isSearching && (
          <Tooltip title={sortLabel}>
            <HeaderButton
              size="small"
              aria-label={sortLabel}
              onClick={handleSort}
            >
              <SortIcons selectedchild={sortType}>
                {IconSouthdMS}
                {IconNorthdMS}
              </SortIcons>
            </HeaderButton>
          </Tooltip>
        )}
        <Tooltip title={Constants.collapseColumn}>
          <HeaderButton
            size="small"
            aria-label={`${Constants.collapseColumn} ${columnData.name}`}
            aria-expanded
            onClick={onToggleCollapse}
          >
            {IconKeyboardDoubleArrowLeftMS}
          </HeaderButton>
        </Tooltip>
      </ColumnHeader>
      <PipelinePaper>
        {pipelineLoading
          ? [1, 2, 3, 4, 5].map((item) => (
              <StyledSkeleton
                key={item}
                variant="rectangular"
                animation="pulse"
              />
            ))
          : workOrders?.map((data: WorkOrderData, index) => {
              const isLastElement = workOrders.length === index + 1;
              return (
                <Link
                  to={`/home/property/${data?.id}/${columnData.link}`}
                  style={{ textDecoration: 'none' }}
                  key={data.id}
                  ref={
                    isLastElement && !isSearching ? lastElementRef : undefined
                  }
                >
                  <Card cardData={data} />
                </Link>
              );
            })}
        {loadingMore && (
          <Box sx={{ margin: '12px' }}>
            <LinearProgress />
          </Box>
        )}
      </PipelinePaper>
    </PipelineBox>
  );
}
